// ─────────────────────────────────────────────────────────────────────────────
// Platform Knowledge Base — Goodera Dashboard Product
//
// Curated answers about how the host product itself works, so the assistant
// can help users navigate the UI, find features, and understand workflows.
// Sourced from the live UI surface (Dashboard Designer, Widget Configuration,
// Filters, Drill-by, Top Nav) — keep this aligned with shipped product.
// ─────────────────────────────────────────────────────────────────────────────

export interface PlatformTopic {
	id: string;
	title: string;
	keywords: string[];
	body: string;
	relatedIds?: string[];
}

export interface PlatformLookupInput {
	query: string;
}

export interface PlatformLookupOutput {
	query: string;
	topics: PlatformTopic[];
	guidance: string;
}

const PLATFORM_TOPICS: PlatformTopic[] = [
	{
		id: "dashboards-overview",
		title: "Dashboards & Dashboard Containers",
		keywords: [
			"dashboard",
			"container",
			"workspace",
			"page",
			"layout",
			"organization",
			"navigate",
		],
		body: `Dashboards are interactive ESG views that pull data from indicators (metrics) tracked in your organization. They are organized into Dashboard Containers (folders that group related dashboards — e.g. "Climate", "Social", "Governance"). Each user navigates to /:organizationId/dashboardBuilder/:dashboardContainerId/:dashboardId.

Roles:
- Viewers see published dashboards.
- Editors can drag widgets, configure filters, save layouts.
- Org admins manage containers, share permissions, and create templates.`,
		relatedIds: ["widgets-overview", "filters-overview"],
	},
	{
		id: "widgets-overview",
		title: "Widgets — chart, table, KPI, map",
		keywords: ["widget", "chart", "graph", "table", "kpi", "indicator", "tile", "card"],
		body: `Widgets are the building blocks of a dashboard. Each widget is bound to one or more indicators (data points) and rendered with a chart library. Common widget types:
- Line / Bar / Stacked Bar / Area / Pie / Donut (Recharts or FusionCharts)
- Data Table (AG-Grid Enterprise — virtualized for large rows)
- KPI tiles (single value with target / vs-prior comparison)
- Map (Leaflet — country / facility-level geographies)
- Heatmap, Funnel, Treemap (FusionCharts)

Widget catalog IDs follow a "<TYPE>_<LIBRARY>" pattern, e.g. LINE_CHART_RECHARTS, STACKED_BAR_FUSION. Each instance has a stable widgetId; the type ID is refWidgetId.`,
		relatedIds: ["dashboards-overview", "drilldown"],
	},
	{
		id: "filters-overview",
		title: "Filters & date ranges",
		keywords: ["filter", "date", "period", "fiscal year", "facility", "region", "scope"],
		body: `Filters apply at the dashboard level and propagate to all widgets that subscribe to the filter dimension. Common filter types:
- Date range (custom or named — FY, calendar year, last 12 months).
- Single-select / multi-select on dimensions (region, facility, business unit).
- Cross-filter: clicking a series in one widget filters other widgets on the same dashboard.

Filter changes don't always refetch — many widgets filter in memory if the data is already loaded. Look at the URL query string to see active filters.`,
		relatedIds: ["dashboards-overview", "drilldown"],
	},
	{
		id: "drilldown",
		title: "Drill-down and drill-by",
		keywords: ["drill", "drilldown", "drill-by", "linked data", "expand", "explore"],
		body: `Drill-down lets you click on a series, bar, slice, or row to see the underlying breakdown by a chosen dimension (e.g. break Scope 2 emissions by facility). Two flavors:
- Drill-by: Right-click → Drill By → choose a dimension. Opens a linked widget.
- Cross-filter: Click a category to filter all peer widgets on that selection.

Drill-down is only available when the underlying indicator has the requested dimension. The chart will preserve the active dashboard filters when drilling.`,
		relatedIds: ["widgets-overview", "filters-overview"],
	},
	{
		id: "indicators-data",
		title: "Indicators (the data layer)",
		keywords: ["indicator", "metric", "data source", "kpi", "fact", "measure"],
		body: `Indicators are the parameterized data sources behind every widget. An indicator is configured once at the org level and then reused across many widgets. Each indicator carries:
- Definition (formula, source dataset, dimensions, units).
- Refresh schedule (manual, daily, on-event).
- Data quality flags (assured, unassured, in-review).
- Owner and approver.

Widgets reference an indicator and may apply per-widget transformations (top N, time aggregation, normalisation). When the indicator updates, every widget bound to it re-renders.`,
		relatedIds: ["widgets-overview", "data-quality"],
	},
	{
		id: "data-quality",
		title: "Data quality & assurance",
		keywords: [
			"assurance",
			"audit",
			"verify",
			"limited assurance",
			"reasonable assurance",
			"data quality",
			"source",
			"trust",
		],
		body: `Each indicator can be tagged with an assurance level:
- Unverified: source value, no third-party check.
- Limited assurance: typical for Scope 1/2 GHG and BRSR Core.
- Reasonable assurance: required for BRSR Core (top 250) by FY2025-26 and CSRD-ESRS over time.

Widgets show an assurance badge when the underlying indicator is assured. Drill into the indicator metadata to see assurer, scope, and report URL.`,
		relatedIds: ["indicators-data"],
	},
	{
		id: "esg-templates",
		title: "ESG dashboard templates",
		keywords: ["template", "starter", "preset", "gri", "tcfd", "csrd", "brsr", "ghg"],
		body: `Pre-built templates accelerate setup. Common ones:
- GHG Inventory (GRI 305 / GHG Protocol layout): Scope 1+2+3 by category.
- TCFD Disclosure: governance, strategy, risk, targets, scenario analysis.
- CSRD-ESRS: E1 Climate, E2 Pollution, E3 Water, E4 Biodiversity, E5 Circular, S1-S4, G1.
- BRSR Principles 1-9: India SEBI mandate, mapped to Goodera indicators.
- SBTi Progress Tracker: target trajectory vs. actual.
- Materiality Heatmap: double materiality (impact vs. financial).

Apply a template via Dashboard Container → Add → From Template. It pre-binds widgets to indicators if the org has matching definitions; otherwise it creates placeholders.`,
		relatedIds: ["dashboards-overview", "indicators-data"],
	},
	{
		id: "exporting",
		title: "Exporting and sharing",
		keywords: ["export", "share", "pdf", "csv", "xlsx", "download", "snapshot", "embed"],
		body: `Each widget has an export menu (top-right ⋯):
- Image (PNG / SVG)
- Underlying data (CSV / XLSX)
- "Snapshot" — locked PDF of the widget at current filters.

At the dashboard level: Share → Public link (read-only, scoped to org) or Embed → iframe snippet.
For audit packs: Export Container → all dashboards as a single PDF report with cover page and timestamps.`,
		relatedIds: ["dashboards-overview"],
	},
	{
		id: "esg-assistant-feature",
		title: "ESG Assistant (this widget)",
		keywords: [
			"assistant",
			"chatbot",
			"ai",
			"chat",
			"help",
			"copilot",
			"claude",
		],
		body: `The ESG Assistant is the floating widget in the bottom-right of every dashboard for org 10489. It reads the dashboard via window.__esgAssistant — a registry that each chart and table populates with its data. The assistant can:
- Answer questions about specific widgets ("what is Scope 2 for FY24 in this chart?").
- Look up ESG framework requirements (GRI, TCFD, CSRD, BRSR, EU Taxonomy, SBTi, CDP).
- Run web searches for current regulations.
- Run materiality / gap / readiness assessments grounded in on-screen data.

The "Show data Claude saw" toggle reveals exactly what the assistant has indexed — full transparency. Conversations are ephemeral (refresh wipes them).`,
		relatedIds: ["widgets-overview"],
	},
];

// ── Lookup ────────────────────────────────────────────────────────────────────

function tokenize(text: string): string[] {
	return text
		.toLowerCase()
		.replace(/[^\w\s\-]/g, " ")
		.split(/\s+/)
		.filter((t) => t.length > 2);
}

function scoreTopic(topic: PlatformTopic, queryTokens: string[]): number {
	let score = 0;
	const haystack = (topic.title + " " + topic.body).toLowerCase();
	for (const tok of queryTokens) {
		if (topic.keywords.some((k) => k.includes(tok) || tok.includes(k))) score += 3;
		if (haystack.includes(tok)) score += 1;
	}
	return score;
}

export function platformLookup(input: PlatformLookupInput): PlatformLookupOutput {
	const tokens = tokenize(input.query);
	const ranked = PLATFORM_TOPICS.map((t) => ({ topic: t, score: scoreTopic(t, tokens) }))
		.filter((r) => r.score > 0)
		.sort((a, b) => b.score - a.score)
		.slice(0, 3)
		.map((r) => r.topic);

	const guidance =
		ranked.length > 0
			? "Below is platform documentation for the user's question. If the user is asking about navigation, point them to the relevant menu / button. If asking about ESG concepts, prefer esg_lookup."
			: "No specific platform topic matched. Briefly summarize what's likely relevant, then offer to look up specific parts.";

	return { query: input.query, topics: ranked, guidance };
}

export { PLATFORM_TOPICS };
